import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CalendarItem, InspectionRecord, InspectionType, VehicleInspection } from '../models/inspection.models';

/** Inspection types, vehicle inspections and their history (logixi-be vehicle-inspection module). */
@Injectable({
  providedIn: 'root'
})
export class VehicleInspectionService {
  private readonly endpoint = environment.api.endpoint;

  constructor(private http: HttpClient) { }

  getTypes(activeOnly: boolean = false): Observable<InspectionType[]> {
    return this.http.get<InspectionType[]>(this.endpoint + 'inspection-type?sort=name,ASC' + (activeOnly ? '&filter=active||eq||1' : ''));
  }

  getType(id: number): Observable<InspectionType> {
    return this.http.get<InspectionType>(this.endpoint + 'inspection-type/' + id);
  }

  saveType(type: InspectionType): Observable<InspectionType> {
    const body = { name: type.name, intervalNumber: Number(type.intervalNumber), intervalUnit: type.intervalUnit, colour: type.colour, defaultDurationMinutes: Number(type.defaultDurationMinutes), active: type.active };
    return type.id > 0
      ? this.http.patch<InspectionType>(this.endpoint + 'inspection-type/' + type.id, body)
      : this.http.post<InspectionType>(this.endpoint + 'inspection-type', body);
  }

  forVehicle(vehicleId: number): Observable<VehicleInspection[]> {
    return this.http.get<VehicleInspection[]>(this.endpoint + 'vehicle-inspection?filter=vehicleId||eq||' + vehicleId + '&sort=nextDueDate,ASC');
  }

  add(vehicleId: number, inspectionTypeId: number, nextDueDate: string): Observable<VehicleInspection> {
    return this.http.post<VehicleInspection>(this.endpoint + 'vehicle-inspection', { vehicleId, inspectionTypeId, nextDueDate });
  }

  updateDue(id: number, nextDueDate: string): Observable<VehicleInspection> {
    return this.http.patch<VehicleInspection>(this.endpoint + 'vehicle-inspection/' + id, { nextDueDate });
  }

  remove(id: number): Observable<unknown> {
    return this.http.delete(this.endpoint + 'vehicle-inspection/' + id);
  }

  /** dueDate is the due date the user saw - the server refuses if it has moved on since. */
  complete(id: number, dueDate: string, doneDate: string, notes: string): Observable<unknown> {
    return this.http.post(this.endpoint + 'vehicle-inspection/' + id + '/complete', { dueDate, doneDate, notes });
  }

  bulkAdd(inspectionTypeId: number, items: { vehicleId: number, nextDueDate: string }[]): Observable<{ added: number[], skipped: number[] }> {
    return this.http.post<{ added: number[], skipped: number[] }>(this.endpoint + 'vehicle-inspection/bulk', { inspectionTypeId, items });
  }

  calendar(from: string, to: string, filters: { inspectionTypeId?: number, vehicleId?: number } = {}): Observable<CalendarItem[]> {
    let url = this.endpoint + 'vehicle-inspection/calendar?from=' + from + '&to=' + to;
    if (filters.inspectionTypeId > 0) { url += '&inspectionTypeId=' + filters.inspectionTypeId; }
    if (filters.vehicleId > 0) { url += '&vehicleId=' + filters.vehicleId; }
    return this.http.get<CalendarItem[]>(url);
  }

  history(vehicleId: number): Observable<InspectionRecord[]> {
    return this.http.get<InspectionRecord[]>(this.endpoint + 'vehicle-inspection-record?filter=vehicleId||eq||' + vehicleId + '&sort=doneDate,DESC');
  }
}
